import React, { useState } from 'react';
import { Box, Text } from '@chakra-ui/react';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';
import { useNavigate } from 'react-router-dom';
import CancelExplanationModal from './CancelExplanationModal';
import ReschedulePopup from './ReschedulePopup';
import { COLLAB_CANCEL_REASONS } from '@foodfluence/shared';

interface CancelOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onConfirm: (explanation: string) => void;
  clientName: string;
}

const CancelOptionsModal: React.FC<CancelOptionsModalProps> = ({ isOpen, onClose, onConfirm, onBack, clientName }) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [isExplainCancelModalOpen, setIsExplainCancelModalOpen] = useState(false);
  const [isReschedulePopupOpen, setIsReschedulePopupOpen] = useState(false);
  const navigate = useNavigate();

  // Bound to the shared reason list rather than re-typed here: the inbox cancel agent classifies
  // a creator's freeform message into these same strings, so both entry points write comparable
  // values to `influencerCancelReason`.
  const cancelOptions = {
    "A": COLLAB_CANCEL_REASONS.scheduleConflict,
    "B": COLLAB_CANCEL_REASONS.brandFit,
    "C": COLLAB_CANCEL_REASONS.compensation,
    "D": 'Other'
  };

  const handleOptionClick = (key: string) => {
    const reason = key;
    setSelectedReason(reason === selectedReason ? null : reason);
  };

  const handleSubmit = () => {
    if (selectedReason === "B" || selectedReason === "C") {
      handleConfirmCancel();
    } else if (selectedReason === "D") {
      setIsExplainCancelModalOpen(true); 
      onClose();
    } else {
      setIsReschedulePopupOpen(true);
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    onConfirm(cancelOptions[selectedReason as keyof typeof cancelOptions]);
    //setIsConfirmRejectModalOpen(false);
    onClose();
  }

  const handleExplainSubmit = (explanation: string) => {
    // Handle the submission of the explanation from ExplainReasonModal
    onConfirm(explanation);
    setIsExplainCancelModalOpen(false);
    onClose();
  };

  return (
    <>
      <ModalShell
        isOpen={isOpen}
        onClose={onClose}
        title={<Text as="span" textStyle="display" fontSize="27px" lineHeight={1.12}>Want to Cancel?</Text>}
        primary={{ label: 'Submit', onClick: handleSubmit, tone: 'danger', isDisabled: !selectedReason }}
        cancelLabel="Keep collab"
      >
        <Text fontSize="15px" lineHeight={1.45} color={ff.body} mb="8px">
          Let us know why!
        </Text>
        <Box role="radiogroup" aria-label="Reason" display="flex" flexDirection="column">
          {Object.entries(cancelOptions).map(([key, option]) => {
            const on = selectedReason === key;
            return (
              <Box
                as="button"
                type="button"
                role="radio"
                aria-checked={on}
                key={key}
                onClick={() => handleOptionClick(key)}
                minH="52px"
                display="flex"
                alignItems="center"
                gap="12px"
                textAlign="left"
                fontSize="15px"
                color={ff.ink}
                borderBottom={`1px solid ${ff.line}`}
              >
                <Box
                  as="span"
                  flex="none"
                  w="22px"
                  h="22px"
                  boxSizing="border-box"
                  borderRadius="999px"
                  border={on ? `7px solid ${ff.ink}` : '2px solid #9A9A9E'}
                />
                {option}
              </Box>
            );
          })}
        </Box>
      </ModalShell>

      <CancelExplanationModal
        isOpen={isExplainCancelModalOpen}
        onClose={() => setIsExplainCancelModalOpen(false)}
        onBack={onBack} // calls onBack to re-open CancelOptionsModal (passed from ScheduleCollabPage)
        onConfirm={handleExplainSubmit}
        clientName={clientName}
      />

      <ReschedulePopup
        isOpen={isReschedulePopupOpen}
        onClose={() => setIsReschedulePopupOpen(false)}
        clientName={clientName}
      />
    </>
  );
};

export default CancelOptionsModal;